import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal, { SectionLabel } from "@/components/Reveal";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import {
  Trophy, Gift, Gem, Users, UserPlus, Link2, ShieldCheck, Send, Lock,
  Check, AlertTriangle, Loader2, Radio, CalendarDays, Timer, History,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FREE_TELEGRAM = "https://t.me/addlist/O9NU4HFXS-cxYzNk";

const EXCLUSIVE_INCLUDES = [
  "Multi-market analysis and session updates",
  "Live TradingView chart updates",
  "BUY/SELL zones and confirmation guidance",
  "ATLAS Academy educational resources",
  "Risk management and strategy guides",
  "Market news breakdowns and priority Q&A",
];

const REQUIREMENTS = [
  { icon: Send, text: "Join ATLAS Analysis on Telegram." },
  { icon: UserPlus, text: "Invite at least 3 genuine new members." },
  { icon: Link2, text: "Use your unique referral link — generated by our Telegram bot when available." },
  { icon: Users, text: "Remain in the group until the draw." },
  { icon: ShieldCheck, text: "Only verified, eligible participants enter the random draw." },
];

const MILESTONES = [100, 200, 300, 400];

const fmtDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

const useCountdown = (target) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (Number.isNaN(diff) || diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return { d, h, m };
};

const Giveaway = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem("atlas_ga_key") || "");
  const [authed, setAuthed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ winners_count: "", prize_duration: "", draw_date: "", entry_deadline: "", enter_url: "", milestone_target: "" });
  const [drawWinners, setDrawWinners] = useState("");
  const [drawNotes, setDrawNotes] = useState("");

  const countdown = useCountdown(data?.draw_date);

  const load = async () => {
    try {
      const { data: d } = await axios.get(`${API}/giveaway`);
      setData(d);
      setError(false);
    } catch {
      setError(true);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (authed && data) {
      setForm({
        winners_count: data.winners_count ?? "",
        prize_duration: data.prize_duration ?? "",
        draw_date: data.draw_date ?? "",
        entry_deadline: data.entry_deadline ?? "",
        enter_url: data.enter_url ?? "",
        milestone_target: data.milestone_target ?? "",
      });
    }
  }, [authed, data?.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const historyMilestones = useMemo(() => new Set((data?.history || []).map((h) => h.milestone)), [data]);

  const unlockAdmin = async () => {
    setBusy(true);
    try {
      await axios.get(`${API}/giveaway/admin/ping`, { headers: { "X-Admin-Key": adminKey } });
      sessionStorage.setItem("atlas_ga_key", adminKey);
      setAuthed(true);
      toast.success("Admin unlocked");
    } catch {
      toast.error("Invalid admin key");
    }
    setBusy(false);
  };

  const adminPost = async (path, body, okMsg) => {
    setBusy(true);
    try {
      await axios.post(`${API}${path}`, body, { headers: { "X-Admin-Key": adminKey } });
      toast.success(okMsg);
      await load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Action failed");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const saveConfig = () => {
    const body = {};
    if (form.winners_count !== "") body.winners_count = Number(form.winners_count);
    if (form.prize_duration) body.prize_duration = form.prize_duration;
    if (form.draw_date) body.draw_date = form.draw_date;
    if (form.entry_deadline) body.entry_deadline = form.entry_deadline;
    if (form.enter_url) body.enter_url = form.enter_url;
    if (form.milestone_target !== "") body.milestone_target = Number(form.milestone_target);
    adminPost("/giveaway/admin/config", body, "Giveaway settings saved");
  };

  const startGiveaway = () => adminPost("/giveaway/admin/config", { status: "active" }, "Giveaway is now LIVE");

  const recordDraw = async () => {
    const winners = drawWinners.split("\n").map((w) => w.trim()).filter(Boolean);
    if (!winners.length) return toast.error("Add at least one winner");
    const ok = await adminPost("/giveaway/admin/draw", { winners, notes: drawNotes || undefined }, "Draw recorded — next milestone activated");
    if (ok) { setDrawWinners(""); setDrawNotes(""); }
  };

  const count = data?.member_count;
  const unlocked = data?.milestone_unlocked;
  const isLive = data?.status === "active";

  return (
    <div className="noise bg-ink text-slate-200 min-h-screen" data-testid="giveaway-page">
      <Nav />
      <main className="pt-32 pb-4">
        {/* ---------- Hero + live counter ---------- */}
        <section className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-16 lg:pb-24 overflow-hidden" data-testid="giveaway-hero">
          <div className="absolute -top-24 right-[-10%] w-[560px] h-[560px] rounded-full bg-gold/10 blur-[130px] pointer-events-none" />
          <Reveal>
            <SectionLabel>Atlas Milestone Giveaways</SectionLabel>
            <h1 className="mt-5 max-w-3xl font-display font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-slate-50 leading-[1.02]" data-testid="giveaway-title">
              Every 100 members,
              <span className="text-gold-gradient"> we give back.</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sm sm:text-base text-slate-400 leading-relaxed">
              A new community giveaway at every 100-member milestone — 100, 200, 300 and onwards.
              The current milestone unlocks the <span className="text-slate-200 font-medium">ATLAS Indicator</span> and{" "}
              <span className="text-gold font-semibold">Lifetime Exclusive Access</span>.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 glass-panel gold-ring chart-frame border border-gold/30 rounded-md p-6 sm:p-10" data-testid="giveaway-counter-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-gold">
                    Current Milestone · {data?.milestone_target ?? 200} Members
                  </span>
                  {isLive && (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border border-emerald-500/40 text-emerald-400 px-2.5 py-1 rounded-sm" data-testid="giveaway-live-badge">
                      <Radio size={11} className="animate-pulse" /> Live Now
                    </span>
                  )}
                  {unlocked && (
                    <span className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] uppercase border border-gold/50 bg-gold/10 text-gold px-2.5 py-1 rounded-sm" data-testid="giveaway-unlocked-badge">
                      <Trophy size={11} /> Milestone Unlocked
                    </span>
                  )}
                </div>
                <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500" data-testid="giveaway-count-status">
                  <span className={`relative flex w-2 h-2`}>
                    <span className={`absolute inline-flex h-full w-full rounded-full ${data?.count_live ? "bg-emerald-400" : "bg-amber-400"} opacity-60 animate-ping`} />
                    <span className={`relative inline-flex rounded-full w-2 h-2 ${data?.count_live ? "bg-emerald-400" : "bg-amber-400"}`} />
                  </span>
                  {data?.count_live ? "Live from Telegram" : "Live counter syncing soon"}
                </span>
              </div>

              <div className="mt-8 flex flex-wrap items-end gap-x-10 gap-y-6">
                <div>
                  <p className="font-display font-extrabold text-6xl sm:text-7xl text-slate-50 tracking-tight" data-testid="giveaway-member-count">
                    {count ?? "—"}
                  </p>
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">Members in ATLAS Analysis</p>
                </div>
                <div className="pb-2">
                  {unlocked ? (
                    <p className="font-display font-bold text-xl text-gold" data-testid="giveaway-remaining">Milestone reached — draw incoming</p>
                  ) : (
                    <p className="font-display font-bold text-xl text-slate-200" data-testid="giveaway-remaining">
                      {data?.remaining != null ? (
                        <><span className="text-gold">{data.remaining}</span> members to go</>
                      ) : (
                        <>Unlocks at <span className="text-gold">{data?.milestone_target ?? 200}</span> members</>
                      )}
                    </p>
                  )}
                  <p className="mt-1 font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">
                    {data?.status === "active" ? "Entries open" : "Giveaway unlocks at the milestone"}
                  </p>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center justify-between font-mono text-[10px] tracking-[0.2em] text-slate-500 uppercase mb-2.5">
                  <span>{data?.milestone_base ?? 100}</span>
                  <span data-testid="giveaway-progress-label">{data?.progress_pct != null ? `${data.progress_pct}%` : ""}</span>
                  <span>{data?.milestone_target ?? 200}</span>
                </div>
                <div className="h-2.5 rounded-full bg-ink/80 border border-line/70 overflow-hidden" data-testid="giveaway-progress-track">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-gold/70 to-gold transition-[width] duration-1000"
                    style={{ width: `${data?.progress_pct ?? 0}%` }}
                    data-testid="giveaway-progress-bar"
                  />
                </div>
              </div>
            </div>
          </Reveal>
          {error && (
            <p className="mt-4 text-sm text-amber-400/90" data-testid="giveaway-load-error">
              Could not load the live giveaway status — refresh in a moment.
            </p>
          )}
        </section>

        {/* ---------- Prizes ---------- */}
        <section className="relative py-16 lg:py-24 bg-panel/30 border-y border-line/60" data-testid="giveaway-prizes">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <SectionLabel>The Prizes</SectionLabel>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-50 leading-tight" data-testid="giveaway-prizes-title">
                What the winner
                <span className="text-gold-gradient"> takes home.</span>
              </h2>
              <p className="mt-4 text-sm text-slate-500 font-mono tracking-[0.15em] uppercase" data-testid="giveaway-winners-count">
                {data?.winners_count ? `${data.winners_count} winner${data.winners_count > 1 ? "s" : ""} this draw` : "Number of winners · to be announced"}
              </p>
            </Reveal>

            <div className="mt-10 grid md:grid-cols-2 gap-6">
              <Reveal delay={0.1}>
                <div className="h-full border border-line rounded-md bg-card2/40 hover:border-gold/40 transition-colors duration-500 p-7 sm:p-9" data-testid="prize-card-indicator">
                  <span className="w-12 h-12 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                    <Gift size={22} className="text-gold" />
                  </span>
                  <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">Prize 01</p>
                  <h3 className="mt-2 font-display font-bold text-2xl text-slate-100">ATLAS Indicator</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                    Full access to the custom-coded TradingView indicator — watchzones, liquidity sweeps,
                    confluence and buy/sell areas, on TradingView's free plan.
                  </p>
                </div>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="relative h-full border border-gold/50 rounded-md bg-gold/5 gold-ring p-7 sm:p-9 overflow-hidden" data-testid="prize-card-exclusive">
                  <span className="absolute top-5 right-5 inline-flex items-center gap-1.5 bg-gold text-ink font-mono text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1.5 rounded-sm" data-testid="prize-lifetime-badge">
                    <Gem size={11} /> Lifetime Exclusive Access
                  </span>
                  <span className="w-12 h-12 rounded-sm border border-gold/40 bg-gold/10 flex items-center justify-center">
                    <Trophy size={22} className="text-gold" />
                  </span>
                  <p className="mt-6 font-mono text-[10px] tracking-[0.3em] uppercase text-gold/80">Prize 02 · Headline</p>
                  <h3 className="mt-2 font-display font-bold text-2xl text-slate-100">ATLAS Exclusive — Forever</h3>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                    Permanent access to the members-only rooms. Never expires, never renews — one win, lifetime in.
                  </p>
                  <ul className="mt-6 space-y-2.5" data-testid="exclusive-includes">
                    {EXCLUSIVE_INCLUDES.map((inc) => (
                      <li key={inc} className="flex items-start gap-2.5 text-sm text-slate-300">
                        <Check size={14} className="text-gold mt-0.5 shrink-0" />
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ---------- How to enter + key details ---------- */}
        <section className="relative py-16 lg:py-24" data-testid="giveaway-enter">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-14">
            <div className="lg:col-span-7">
              <Reveal>
                <SectionLabel>How To Enter</SectionLabel>
                <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-50 leading-tight" data-testid="giveaway-requirements-title">
                  Five steps.
                  <span className="text-gold-gradient"> Genuine entries only.</span>
                </h2>
              </Reveal>
              <div className="mt-9 space-y-5" data-testid="giveaway-requirements">
                {REQUIREMENTS.map((r, i) => (
                  <Reveal key={r.text} delay={i * 0.07}>
                    <div className="flex items-start gap-4" data-testid={`giveaway-requirement-${i + 1}`}>
                      <span className="w-10 h-10 shrink-0 rounded-sm border border-gold/30 bg-gold/5 flex items-center justify-center">
                        <r.icon size={16} className="text-gold" />
                      </span>
                      <p className="pt-2 text-sm sm:text-base text-slate-300 leading-relaxed">
                        <span className="font-mono text-[10px] text-gold/60 mr-2">{String(i + 1).padStart(2, "0")}</span>
                        {r.text}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </div>
              <Reveal delay={0.35}>
                <div className="mt-8 flex items-start gap-3 border border-amber-500/30 bg-amber-500/5 rounded-md px-5 py-4" data-testid="giveaway-anticheat-note">
                  <AlertTriangle size={16} className="text-amber-400 mt-0.5 shrink-0" />
                  <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                    Fake accounts, duplicate referrals and manipulated entries are disqualified.
                    Every entry is verified before the random draw.
                  </p>
                </div>
              </Reveal>
              <Reveal delay={0.4}>
                <div className="mt-9">
                  <a
                    href={data?.enter_url || FREE_TELEGRAM}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="giveaway-enter-button"
                    className="inline-flex items-center gap-2.5 bg-gold text-ink font-bold text-sm tracking-wide px-8 py-4 rounded-sm hover:bg-gold-bright transition-colors duration-300"
                  >
                    <Send size={16} />
                    {data?.enter_url ? "Enter the Giveaway" : "Join ATLAS Analysis to Enter"}
                  </a>
                  {!data?.enter_url && (
                    <p className="mt-3 text-[11px] text-slate-500">
                      The referral-tracking bot link lands here once it's live — for now, entry starts in the free group.
                    </p>
                  )}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                <div className="border border-line rounded-md bg-card2/40 p-6 sm:p-8" data-testid="giveaway-details-card">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500 mb-6">Key Details</p>
                  <dl className="space-y-5">
                    {[
                      { label: "Winners", value: data?.winners_count ? `${data.winners_count}` : null, testid: "giveaway-detail-winners" },
                      { label: "Prize duration", value: data?.prize_duration, testid: "giveaway-detail-duration" },
                      { label: "Entry deadline", value: fmtDate(data?.entry_deadline), testid: "giveaway-detail-deadline" },
                      { label: "Draw date", value: fmtDate(data?.draw_date), testid: "giveaway-detail-draw" },
                    ].map((d) => (
                      <div key={d.label} className="flex items-center justify-between gap-4 border-b border-line/50 pb-4">
                        <dt className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500">{d.label}</dt>
                        <dd className={`text-sm font-medium ${d.value ? "text-slate-100" : "text-slate-600"}`} data-testid={d.testid}>
                          {d.value || "To be announced"}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  {countdown && (
                    <div className="mt-6 border border-gold/30 bg-gold/5 rounded-sm px-5 py-4 text-center" data-testid="giveaway-countdown">
                      <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold/80 flex items-center justify-center gap-2">
                        <Timer size={12} /> Draw countdown
                      </p>
                      <p className="mt-2 font-display font-extrabold text-2xl text-slate-50" data-testid="giveaway-countdown-timer">
                        {countdown.d}d · {countdown.h}h · {countdown.m}m
                      </p>
                    </div>
                  )}
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* ---------- Milestone history ---------- */}
        <section className="relative py-16 lg:py-24 bg-panel/30 border-y border-line/60" data-testid="giveaway-history">
          <div className="max-w-7xl mx-auto px-5 sm:px-8">
            <Reveal>
              <SectionLabel>Milestone History</SectionLabel>
              <h2 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-4xl text-slate-50 leading-tight" data-testid="giveaway-history-title">
                Past draws
                <span className="text-gold-gradient"> & what's next.</span>
              </h2>
            </Reveal>
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {MILESTONES.map((m, i) => {
                const past = historyMilestones.has(m);
                const entry = (data?.history || []).find((h) => h.milestone === m);
                const current = m === data?.milestone_target;
                return (
                  <Reveal key={m} delay={i * 0.08}>
                    <div
                      className={`h-full rounded-md border p-6 transition-colors duration-500 ${
                        current ? "border-gold/50 bg-gold/5 gold-ring" : past ? "border-line bg-card2/40" : "border-line/60 bg-ink/40"
                      }`}
                      data-testid={`giveaway-milestone-${m}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-display font-extrabold text-2xl text-slate-100">{m}</p>
                        <span className={`font-mono text-[9px] tracking-[0.2em] uppercase px-2 py-1 rounded-sm border ${
                          past ? "border-emerald-500/40 text-emerald-400" : current ? "border-gold/40 text-gold" : "border-line text-slate-600"
                        }`}>
                          {past ? "Drawn" : current ? (unlocked ? "Unlocked" : "In Progress") : "Locked"}
                        </span>
                      </div>
                      <p className="mt-1.5 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Members</p>
                      {past && entry ? (
                        <div className="mt-4 pt-4 border-t border-line/60">
                          <p className="font-mono text-[9px] tracking-[0.25em] uppercase text-slate-500 mb-2 flex items-center gap-1.5">
                            <History size={10} /> Winner{entry.winners.length > 1 ? "s" : ""} · {fmtDate(entry.drawn_at)}
                          </p>
                          {entry.winners.map((w) => (
                            <p key={w} className="text-sm text-gold font-medium">{w}</p>
                          ))}
                        </div>
                      ) : current ? (
                        <p className="mt-4 pt-4 border-t border-line/60 text-xs text-slate-400 leading-relaxed">
                          This draw is live now — winners announced in ATLAS Analysis.
                        </p>
                      ) : (
                        <p className="mt-4 pt-4 border-t border-line/60 text-xs text-slate-600 leading-relaxed flex items-center gap-1.5">
                          <Lock size={11} /> Unlocks when the community gets here.
                        </p>
                      )}
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>

        {/* ---------- Admin ---------- */}
        <section className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
          <div className="flex justify-center">
            <button
              onClick={() => setShowAdmin(!showAdmin)}
              data-testid="giveaway-admin-toggle"
              className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-600 hover:text-gold transition-colors"
            >
              Admin
            </button>
          </div>
          {showAdmin && !authed && (
            <div className="mt-6 max-w-sm mx-auto border border-line rounded-md bg-card2/40 p-6" data-testid="giveaway-admin-login">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-4">Owner access</p>
              <input
                type="password"
                value={adminKey}
                onChange={(e) => setAdminKey(e.target.value)}
                placeholder="Admin key"
                data-testid="giveaway-admin-key-input"
                className="w-full bg-ink/70 border border-line rounded-sm px-3 py-2.5 text-sm text-slate-100 focus:outline-none focus:border-gold/60 transition-colors"
              />
              <button
                onClick={unlockAdmin}
                disabled={busy || !adminKey}
                data-testid="giveaway-admin-login-button"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-gold-bright transition-colors disabled:opacity-60"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />}
                Unlock
              </button>
            </div>
          )}
          {showAdmin && authed && (
            <div className="mt-6 max-w-2xl mx-auto border border-gold/30 rounded-md bg-card2/40 p-6 sm:p-8" data-testid="giveaway-admin-panel">
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-gold mb-6 flex items-center gap-2">
                <CalendarDays size={12} /> Giveaway Controls
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { key: "winners_count", label: "WINNERS COUNT", type: "number", testid: "admin-input-winners" },
                  { key: "prize_duration", label: "PRIZE DURATION", type: "text", testid: "admin-input-duration" },
                  { key: "draw_date", label: "DRAW DATE", type: "datetime-local", testid: "admin-input-draw-date" },
                  { key: "entry_deadline", label: "ENTRY DEADLINE", type: "datetime-local", testid: "admin-input-deadline" },
                  { key: "enter_url", label: "ENTER URL (t.me bot link)", type: "text", testid: "admin-input-enter-url" },
                  { key: "milestone_target", label: "MILESTONE TARGET", type: "number", testid: "admin-input-milestone" },
                ].map((f) => (
                  <label key={f.key} className="block">
                    <span className="font-mono text-[9px] tracking-[0.2em] text-slate-500">{f.label}</span>
                    <input
                      type={f.type}
                      value={form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={f.testid}
                      className="mt-1 w-full bg-ink/70 border border-line rounded-sm px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-gold/60 transition-colors"
                    />
                  </label>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <button onClick={saveConfig} disabled={busy} data-testid="giveaway-admin-save" className="inline-flex items-center gap-2 bg-gold text-ink font-semibold text-sm px-5 py-2.5 rounded-sm hover:bg-gold-bright transition-colors disabled:opacity-60">
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Save Settings
                </button>
                {!isLive && (
                  <button onClick={startGiveaway} disabled={busy} data-testid="giveaway-admin-start" className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-400 font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-emerald-500/10 transition-colors disabled:opacity-60">
                    <Radio size={14} /> Start Giveaway
                  </button>
                )}
              </div>
              <div className="mt-8 pt-6 border-t border-line/60">
                <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-slate-500 mb-4">Record Draw (moves to next milestone)</p>
                <textarea
                  value={drawWinners}
                  onChange={(e) => setDrawWinners(e.target.value)}
                  placeholder={"Winners, one per line\ne.g. @username"}
                  rows={3}
                  data-testid="giveaway-admin-winners-input"
                  className="w-full bg-ink/70 border border-line rounded-sm px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-gold/60 transition-colors"
                />
                <input
                  type="text"
                  value={drawNotes}
                  onChange={(e) => setDrawNotes(e.target.value)}
                  placeholder="Notes (optional)"
                  data-testid="giveaway-admin-notes-input"
                  className="mt-3 w-full bg-ink/70 border border-line rounded-sm px-3 py-2 text-sm text-slate-100 focus:outline-none focus:border-gold/60 transition-colors"
                />
                <button onClick={recordDraw} disabled={busy} data-testid="giveaway-admin-draw" className="mt-4 inline-flex items-center gap-2 border border-gold/40 text-gold font-medium text-sm px-5 py-2.5 rounded-sm hover:bg-gold/10 transition-colors disabled:opacity-60">
                  <Trophy size={14} /> Record Draw & Advance Milestone
                </button>
              </div>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Giveaway;
